<template>
  <div
    class="group/assigned-people relative -m-1 flex flex-row rounded-full border bg-white p-1"
    :class="[
      canBeDropable && (isDraggingOver || dragOverResource)
        ? 'border-dashed'
        : ''
    ]"
    :draggable="draggable && editable"
    @dragstart="handleDragStart"
    @dragend="handleDragEnd"
    @dragover="handleDragOver"
  >
    <div
      v-show="editable && (canBeDropable || isDraggingOver || dragOverResource)"
      class="resource-material-dropping-zone absolute inset-0 z-90"
      @dragover="handleDragOver"
      @dragleave="handleDragLeave"
      @drop.prevent="handleDropping"
    >
      &nbsp;
    </div>

    <div>
      <MaterialResourceAvatarPopover
        :material-resource-id="assignedMaterialResource.id"
        :disponibility="
          assignedMaterialResource?.disponibility?.assignement_status
        "
        :size="size"
      >
        <template v-if="editable" #panel="{ close }">
          <div class="flex flex-col divide-y divide-slate-200">
            <div class="pb-3">
              <BaseButton
                title="copy same défunt"
                :icon="'copy'"
                class="w-full"
                size="small"
                @click="copyMaterialResourceToDossierEvents(close)"
              />
            </div>
            <div class="flex flex-col gap-2 py-3">
              <SelectAssignmentStatus
                v-model="internalAssignementStatus"
                edition-mode
                required
                size="small"
              />
              <BaseButton
                v-show="needSave"
                class="w-full"
                :title="$t('button.save')"
                size="small"
                theme-color="bg-secondary text-contrast-secondary"
                @click="() => saveMaterialResource(close)"
              />
            </div>
            <div class="pt-3">
              <BaseButton
                class="w-full"
                :title="$t('button.delete')"
                size="extra-small"
                color="danger"
                @click="() => removeMaterialResourceFromEvent(close)"
              />
            </div>
          </div>
        </template>
      </MaterialResourceAvatarPopover>
    </div>
    <div
      class="flex flex-1"
      :class="[
        assignedMaterialResource?.disponibility?.assigned_people.length > 0
          ? 'ml-3'
          : ''
      ]"
    >
      <AssignedPerson
        v-for="assignedPerson in assignedMaterialResource?.disponibility
          ?.assigned_people"
        :key="assignedPerson.id"
        :assigned-person="assignedPerson"
        :editable="editable"
        :size="size"
        :event-id="assignedMaterialResource.disponibility.event_id"
        @event-updated="(event) => $emit('event-updated', event)"
        @fetch-events="$emit('fetch-events')"
      />
    </div>
    <div
      v-if="
        editable &&
        assignedMaterialResource?.disponibility?.assignedPeople?.length === 0
      "
    >
      <div
        class="ml-1 flex h-9 w-9 flex-row items-center justify-center rounded-full border text-slate-600"
      >
        <FontAwesomeIcon :icon="['fal', 'user-plus']" />
      </div>
    </div>
    <div
      v-if="
        editable && !(canBeDropable && (isDraggingOver || dragOverResource))
      "
      class="flex w-0 shrink-0 cursor-grab items-center justify-center overflow-hidden text-slate-400 transition-all group-hover/assigned-people:text-slate-900 group-hover/event-card:w-6 group-hover/event-card:p-1"
    >
      <FontAwesomeIcon :icon="['far', 'grip-vertical']" fixed-width />
    </div>

    <ConfirmModal ref="availabilityAlertModal" />
  </div>
</template>

<script>
import SelectAssignmentStatus from '@c/InitialStateFormComponent/Enums/SelectAssignmentStatus.vue'
import BaseButton from '@c/addf-package/components/BaseButton/BaseButton.vue'
import { mapActions, mapGetters } from 'vuex'
import MaterialResourceAvatarPopover from '@c/Avatar/Popover/MaterialResourceAvatarPopover.vue'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import AssignedPerson from '@c/Dashboard/Content/Planning/components/AssignedPerson.vue'
import ConfirmModal from '@c/addf-package/components/modal/ConfirmModal.vue'

export default {
  name: 'AssignedMaterialResource',
  components: {
    ConfirmModal,
    AssignedPerson,
    FontAwesomeIcon,
    MaterialResourceAvatarPopover,
    BaseButton,
    SelectAssignmentStatus
  },
  props: {
    assignedMaterialResource: {
      type: Object,
      required: true
    },
    event: {
      type: Object,
      required: true
    },
    canBeDropable: {
      type: Boolean,
      required: false,
      default: false
    },
    dragOverResource: {
      type: Boolean,
      required: false,
      default: false
    },
    editable: {
      type: Boolean,
      required: false,
      default: false
    },
    size: {
      type: String,
      required: false,
      default: 'medium',
      validator: (value) =>
        ['extra-small', 'small', 'medium', 'large', 'extra-large'].indexOf(
          value
        ) !== -1
    }
  },
  emits: [
    'update:assigned-material-resource',
    'event-updated',
    'update:drag-over-resource',
    'fetch-events'
  ],
  data() {
    return {
      internalAssignementStatus:
        this.assignedMaterialResource?.disponibility?.assignement_status,
      draggable: true,
      availabilityAlertModal: false,
      isDraggingOver: false
    }
  },
  computed: {
    ...mapGetters({
      dataTransferObject: 'planning/getDataTransferObject'
    }),
    isUnavailable() {
      if (!this.dataTransferObject?.availabilityItems) {
        return false
      }

      return (
        (this.dataTransferObject.availabilityItems || []).filter((item) => {
          if (item.unavailable) {
            if (
              this.event.date_from >= item.start_at &&
              this.event.date_from <= item.end_at
            ) {
              return true
            } else if (
              this.event.date_to >= item.start_at &&
              this.event.date_to <= item.end_at
            ) {
              return true
            }
          }

          return false
        }).length > 0
      )
    },
    localAssignedMaterialResource: {
      get() {
        return this.assignedMaterialResource
      },
      set(value) {
        this.$emit('update:assigned-material-resource', value)
      }
    },
    localDragOverResource: {
      get() {
        return this.dragOverResource
      },
      set(value) {
        this.$emit('update:drag-over-resource', value)
      }
    },
    needSave() {
      return (
        this.localAssignedMaterialResource.disponibility.assignement_status !==
        this.internalAssignementStatus
      )
    }
  },
  methods: {
    ...mapActions({
      processUpdateAssignedMaterialResourceAction:
        'planning/updateAssignedMaterialResource',
      processRemoveAssignedMaterialResourceAction:
        'planning/removeAssignedMaterialResource',
      processSetDataTransferObjectAction: 'planning/setDataTransferObject',
      processAddAssignedPersonToMaterialResourceAction:
        'planning/addAssignedPersonToMaterialResource',
      processCopyMaterialResourceDossierAction:
        'planning/copyMaterialResourceDossier'
    }),
    saveMaterialResource(close) {
      this.processUpdateAssignedMaterialResourceAction({
        material_resource_id: this.assignedMaterialResource.id,
        event_id: this.event.id,
        data: {
          assignement_status: this.internalAssignementStatus
        }
      }).then((response) => {
        this.$h.toastSuccess(this.$t('toasts.updated'))
        this.$emit('event-updated', response.event)
        close()
      })
    },
    removeMaterialResourceFromEvent(material_resource, close) {
      this.processRemoveAssignedMaterialResourceAction({
        event_id: this.event.id,
        material_resource_id: this.assignedMaterialResource.disponibility.id
      })
        .then((response) => {
          this.$emit('event-updated', response.event)
          this.$emit('fetch-events')
          this.$h.toastSuccess(this.$t('toasts.updated'))
          close()
        })
        .catch((_) => {
          this.$emit('fetch-events')
        })
    },
    copyMaterialResourceToDossierEvents(material_resource, close) {
      this.processCopyMaterialResourceDossierAction({
        event_id: this.event.id,
        material_resource_id: this.assignedMaterialResource.disponibility.id
      })
        .then((response) => {
          this.$emit('fetch-events')
          this.$h.toastSuccess(this.$t('toasts.updated'))
          close()
        })
        .catch((_) => {
          this.$emit('fetch-events')
        })
    },
    handleDragStart(_event) {
      this.processSetDataTransferObjectAction({
        type: 'assigned-material-resource',
        event: this.event.id,
        material_resource: this.assignedMaterialResource.id
      })
    },
    handleDragEnd(_event) {
      this.draggable = true
      this.processSetDataTransferObjectAction(null)
    },
    handleDropping(event) {
      event.preventDefault()
      const dataTransferObject = { ...this.dataTransferObject }

      if (this.isUnavailable) {
        const isUser = dataTransferObject.type === 'user'
        this.$refs.availabilityAlertModal
          .show({
            title: this.$t('alert.absence_alert'),
            message: this.$t(
              `alert.absence_alert_message_${dataTransferObject.type}`,
              {
                name: isUser
                  ? dataTransferObject.model.full_name
                  : this.$trans(dataTransferObject.model.name),
                event: this.event.title
              }
            ),
            okButton: this.$t('button.add_anyway')
          })
          .then((response) => {
            if (response) {
              this.applyHandleDropping(dataTransferObject)
            }
          })
      } else {
        this.applyHandleDropping(dataTransferObject)
      }
    },
    applyHandleDropping(dataTransferObject) {
      this.handleDragLeave()

      this.processAddAssignedPersonToMaterialResourceAction({
        event_id: this.event.id,
        events_material_resource_id:
          this.assignedMaterialResource.disponibility.id,
        user_id: dataTransferObject.model?.id
      })
        .then((response) => {
          this.$h.toastSuccess(this.$t('global.added'))
          this.$emit('event-updated', response.event)
          this.$emit('fetch-events')
        })
        .catch((_) => {
          console.log('error')
        })
    },
    handleDragOver(event) {
      event.dataTransfer.dropEffect = 'copy'
      this.draggable = false
      this.isDraggingOver = true
      this.localDragOverResource = true
    },
    handleDragLeave() {
      this.draggable = true
      this.isDraggingOver = false
      this.localDragOverResource = false
    }
  }
}
</script>
